import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => resolve(); // still resolve to avoid blocking
    document.body.appendChild(script);
  });
};

const formatSpaceDisplay = (mb) => {
  if (mb == null || mb === '') return '—';
  const n = Number(mb);
  if (isNaN(n)) return '—';
  if (n >= 1024) return (n / 1024).toFixed(1) + ' GB';
  return n + ' MB';
};

const StatusBadge = ({ status }) => {
  const map = {
    active:           'bg-emerald-100 text-emerald-700 border-emerald-200',
    trial:            'bg-blue-100 text-blue-700 border-blue-200',
    pending_approval: 'bg-amber-100 text-amber-700 border-amber-200',
    expired:          'bg-slate-100 text-slate-500 border-slate-200',
  };
  return (
    <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full border capitalize ${map[status] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
};

const UserSubscription = () => {
  const [subscription, setSubscription] = useState(null);
  const [trialConfig, setTrialConfig] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [subscribingPlanId, setSubscribingPlanId] = useState(null);
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [subscribeMessage, setSubscribeMessage] = useState('');
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMe();
    fetchPlans();
    fetchHistory();
  }, []);

  const fetchMe = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/me`);
      setSubscription(res.data.subscription ?? null);
      setTrialConfig(res.data.trial_config ?? null);
    } catch (_) {
      setSubscription(null);
      setTrialConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_URL}/subscriptions/plans`);
      setAvailablePlans(Array.isArray(res.data) ? res.data : []);
    } catch (_) { setAvailablePlans([]); }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/subscriptions/history`);
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (_) { setHistory([]); }
  };

  const handleSubscribe = async (planId) => {
    setSubscribeMessage('');
    setSubscribeSuccess(false);
    setSubscribingPlanId(planId);
    try {
      const res = await axios.post(`${API_URL}/subscriptions/subscribe`, { plan_id: planId });
      setSubscribeMessage(res.data?.message || 'Request submitted. Pending admin approval.');
      setSubscribeSuccess(true);
      fetchMe();
      fetchHistory();
    } catch (err) {
      setSubscribeMessage(err.response?.data?.error || 'Failed to submit subscription.');
      setSubscribeSuccess(false);
    } finally {
      setSubscribingPlanId(null);
    }
  };

  const handlePayWithRazorpay = async (plan) => {
    const planId = plan.id;
    setSubscribeMessage('');
    setSubscribeSuccess(false);
    setPayingPlanId(planId);
    try {
      await loadRazorpayScript();
      if (!window.Razorpay) {
        setSubscribeMessage('Payment gateway could not be loaded. Try again.');
        return;
      }
      const orderRes = await axios.post(`${API_URL}/payments/create-order`, { plan_id: planId });
      const { order_id, amount, currency, key_id, plan_name } = orderRes.data;
      const options = {
        key: key_id,
        amount,
        currency,
        order_id,
        name: 'Pixel Event Photo',
        description: plan_name || 'Subscription',
        theme: { color: '#7c3aed' },
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API_URL}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setSubscribeMessage(verifyRes.data?.message || 'Payment successful. Subscription activated.');
            setSubscribeSuccess(true);
            fetchMe();
            fetchHistory();
          } catch (err) {
            setSubscribeMessage(err.response?.data?.error || 'Payment verification failed.');
            setSubscribeSuccess(false);
          } finally {
            setPayingPlanId(null);
          }
        },
        modal: {
          ondismiss: () => setPayingPlanId(null),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setSubscribeMessage(err.response?.data?.error || 'Failed to start payment.');
      setSubscribeSuccess(false);
      setPayingPlanId(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto flex justify-center items-center min-h-[40vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Loading subscription…</p>
        </div>
      </div>
    );
  }

  const subAllowed = subscription?.allowed === true;
  const subStatus = subscription?.subscription?.status;
  const subExpires = subscription?.subscription?.expires_at;
  const currentPlanId = subscription?.subscription?.plan_id != null ? Number(subscription.subscription.plan_id) : null;
  const planName = subscription?.plan?.name;
  const isPending = subStatus === 'pending_approval';

  const expiresAt = subExpires ? new Date(subExpires) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)) : null;
  const expiryGrace = subscription?.expiry_grace;
  const inGracePeriod = expiryGrace?.in_grace_period === true;
  const daysUntilDeletion = expiryGrace?.days_until_deletion ?? null;
  const gracePeriodEndsAt = expiryGrace?.grace_period_ends_at;
  const expiringSoon = (expiryGrace?.expires_in_days != null && expiryGrace.expires_in_days >= 1 && expiryGrace.expires_in_days <= 7);

  return (
    <div className="max-w-3xl mx-auto space-y-5">

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Subscription</h1>
        <p className="text-sm text-slate-500 mt-0.5">Your current plan, expiry, and available plans.</p>
      </div>

      {/* Alert banners */}
      {!subAllowed && subscription != null && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-800">
          <svg className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div>
            <p className="font-semibold">{subscription.reason || 'Subscription expired or not active.'}</p>
            <p className="text-amber-700 mt-0.5">Upload, create event, and verification are disabled until you subscribe again.</p>
          </div>
        </div>
      )}

      {inGracePeriod && (
        <div className="flex items-start gap-3 px-4 py-3 bg-rose-50 border border-rose-200 rounded-2xl text-sm text-rose-800">
          <svg className="h-4 w-4 shrink-0 mt-0.5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div>
            <p className="font-semibold">Data deletion warning</p>
            <p className="text-rose-700 mt-0.5">
              If plan is not renewed within {daysUntilDeletion} day{daysUntilDeletion !== 1 ? 's' : ''}, all your events, batches and photos will be permanently deleted.
              {gracePeriodEndsAt && ` Deletion date: ${new Date(gracePeriodEndsAt).toLocaleDateString(undefined, { dateStyle: 'long' })}.`}
            </p>
          </div>
        </div>
      )}

      {isPending && (
        <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-2xl text-sm text-blue-800">
          <svg className="h-4 w-4 shrink-0 mt-0.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-semibold">Pending admin approval</p>
            <p className="text-blue-700 mt-0.5">Your subscription request is awaiting approval. You can use the trial in the meantime (if applicable).</p>
          </div>
        </div>
      )}

      {/* Current plan card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">My Plan</h2>
        </div>

        {subscription == null ? (
          <div className="px-5 py-6 text-sm text-slate-500">Unable to load subscription info.</div>
        ) : (
          <div className="px-5 py-4">
            {subAllowed && !isPending ? (
              <div className="flex flex-wrap items-start gap-4 justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-lg font-bold text-slate-900">{planName || 'Trial'}</span>
                    <StatusBadge status={subStatus} />
                    {trialConfig && (subStatus === 'trial' || !planName) && (
                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        {trialConfig.trial_space_mb} MB · {trialConfig.trial_days} days
                      </span>
                    )}
                  </div>
                  {subExpires && (
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                      <span className="text-slate-500">
                        Expires {new Date(subExpires).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                      </span>
                      {daysLeft !== null && (
                        <span className={`font-semibold ${daysLeft < 0 ? 'text-rose-600' : daysLeft <= 7 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago` : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
                        </span>
                      )}
                    </div>
                  )}
                  {expiringSoon && (
                    <p className="text-xs text-amber-600 mt-1.5">Renew before expiry — data may be deleted 7 days after expiry.</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">No active plan. Subscribe below to get started.</p>
            )}
          </div>
        )}
      </div>

      {/* Available plans */}
      {availablePlans.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Available Plans</h2>
            <p className="text-xs text-slate-400 mt-0.5">Pay with Razorpay for instant activation, or request for admin approval.</p>
          </div>
          <div className="p-5">
            {subscribeMessage && (
              <div className={`mb-4 px-4 py-3 rounded-xl border text-sm ${subscribeSuccess ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                {subscribeMessage}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {availablePlans.map((p) => {
                const hasPrice = p.amount_inr != null && !isNaN(p.amount_inr) && Number(p.amount_inr) >= 1;
                const isCurrentActivePlan = subAllowed && currentPlanId != null && currentPlanId === p.id;
                const isPaying = payingPlanId === p.id;
                const isSubmitting = subscribingPlanId === p.id;
                return (
                  <div key={p.id} className="flex flex-col justify-between gap-4 p-4 rounded-2xl border border-slate-200 bg-slate-50 hover:border-violet-300 hover:bg-violet-50/30 transition-all">
                    <div>
                      <p className="font-bold text-slate-900 text-base">{p.name}</p>
                      <div className="mt-2 space-y-1">
                        {hasPrice && (
                          <p className="text-lg font-bold text-violet-700">₹{Number(p.amount_inr).toLocaleString('en-IN')}</p>
                        )}
                        {!hasPrice && (
                          <p className="text-sm text-slate-500">Price on request</p>
                        )}
                        <p className="text-xs text-slate-500">
                          {formatSpaceDisplay(p.space_mb)} storage · {p.duration_days} days
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {hasPrice && isCurrentActivePlan && (
                        <div className="w-full px-4 py-2.5 text-sm font-semibold rounded-xl bg-emerald-100 text-emerald-800 border border-emerald-200 text-center">
                          Current plan
                          {subExpires && (
                            <span className="block text-xs font-normal mt-0.5">
                              Active until {new Date(subExpires).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                            </span>
                          )}
                        </div>
                      )}
                      {hasPrice && !isCurrentActivePlan && (
                        <button
                          type="button"
                          onClick={() => handlePayWithRazorpay(p)}
                          disabled={isPaying || isSubmitting || isPending}
                          className="w-full px-4 py-2.5 text-sm font-semibold text-white rounded-xl disabled:opacity-50 transition-all hover:shadow-md active:scale-95"
                          style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}
                        >
                          {isPaying ? 'Opening…' : `Pay ₹${Number(p.amount_inr).toLocaleString('en-IN')}`}
                        </button>
                      )}
                      {!isCurrentActivePlan && (
                        <button
                          type="button"
                          onClick={() => handleSubscribe(p.id)}
                          disabled={isPaying || isSubmitting || isPending}
                          className={`w-full px-4 py-2 text-sm font-semibold rounded-xl disabled:opacity-50 transition-all border ${hasPrice ? 'border-slate-200 text-slate-700 bg-white hover:bg-slate-50' : 'text-white hover:shadow-md active:scale-95'}`}
                          style={!hasPrice ? { background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' } : {}}
                        >
                          {isSubmitting ? 'Submitting…' : isPending ? 'Pending approval' : hasPrice ? 'Request approval instead' : 'Subscribe'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Subscription history */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">History</h2>
          <p className="text-xs text-slate-400 mt-0.5">All past and current subscriptions.</p>
        </div>
        {history.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-400">No subscription history yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Plan</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Storage</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Started</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Expires</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((h) => (
                  <tr key={h.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3 text-sm font-semibold text-slate-800">{h.plan_name || '—'}</td>
                    <td className="px-5 py-3"><StatusBadge status={h.status} /></td>
                    <td className="px-5 py-3 text-sm text-slate-600">{formatSpaceDisplay(h.space_mb)}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{h.started_at ? new Date(h.started_at).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—'}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{h.expires_at ? new Date(h.expires_at).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSubscription;
